import { useQuery } from 'convex/react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Loader2, Building2, User } from 'lucide-react'
import { api } from '../../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'

function ExternalLinkPill({ href, label }: { href?: string; label: string }) {
  if (!href) return null
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500 hover:text-neutral-900 transition-colors"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  )
}

function ChipList({ items, tone = 'neutral' }: { items: string[]; tone?: 'neutral' | 'emerald' }) {
  if (!items.length) return <span className="text-xs text-neutral-400">—</span>
  const cls =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-neutral-100 text-neutral-700 border-neutral-200'
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((s) => (
        <span key={s} className={`px-2 py-0.5 text-xs rounded-full border ${cls}`}>
          {s}
        </span>
      ))}
    </div>
  )
}

export default function AdminInvestorDetailPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const { slug } = useParams<{ slug: string }>()

  const data = useQuery(api.vcdb.getInvestor, slug ? { slug } : 'skip')

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }
  if (!data) {
    return (
      <div className="p-6 flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }
  if (data === null) {
    return (
      <div className="p-6 space-y-2">
        <Link to="/admin/investors" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
          <ArrowLeft className="h-4 w-4" /> All investors
        </Link>
        <div className="text-sm text-neutral-500">Investor not found.</div>
      </div>
    )
  }

  const { investor, funds, teammates } = data

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <Link to="/admin/investors" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700">
        <ArrowLeft className="h-4 w-4" /> All investors
      </Link>

      <div className="flex items-start gap-4">
        {investor.headshotUrl ? (
          <img src={investor.headshotUrl} alt={investor.name} className="h-20 w-20 rounded-full object-cover bg-neutral-100" />
        ) : (
          <div className="h-20 w-20 rounded-full bg-neutral-100 flex items-center justify-center">
            <User className="h-8 w-8 text-neutral-400" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold text-neutral-900">{investor.name}</h1>
          {investor.title && <div className="text-sm text-neutral-600 mt-0.5">{investor.title}</div>}
          {investor.location && <div className="text-xs text-neutral-500 mt-0.5">{investor.location}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <ExternalLinkPill href={investor.linkedinUrl} label="LinkedIn" />
            <ExternalLinkPill href={investor.twitterUrl} label="Twitter" />
            <ExternalLinkPill href={investor.crunchbaseUrl} label="Crunchbase" />
            <ExternalLinkPill href={investor.websiteUrl} label="Website" />
            <ExternalLinkPill href={investor.sourceUrl} label="vcsheet" />
            {investor.email && (
              <a
                href={`mailto:${investor.email}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500 hover:text-neutral-900"
              >
                {investor.email}
              </a>
            )}
          </div>
        </div>
      </div>

      {investor.bioShort && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Summary</h2>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{investor.bioShort}</p>
        </div>
      )}

      {investor.bio && investor.bio !== investor.bioShort && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">About</h2>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{investor.bio}</p>
        </div>
      )}

      {investor.interests && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Interests</h2>
          <p className="text-xs text-neutral-500 mb-2">What they've been talking about across their social media accounts</p>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line">{investor.interests}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stat label="Avg check">
          <span className="text-sm text-neutral-900 font-medium">{investor.avgCheckText ?? '—'}</span>
        </Stat>
        <Stat label="Leads rounds">
          <span className="text-sm text-neutral-900 font-medium">
            {investor.leadsRounds === undefined ? '—' : investor.leadsRounds ? 'Yes' : 'No'}
          </span>
        </Stat>
        <Stat label="Stages">
          <ChipList items={investor.stages} />
        </Stat>
        <Stat label="Geo">
          <ChipList items={investor.geo} />
        </Stat>
        <Stat label="Sectors" wide>
          <ChipList items={investor.sectors} />
        </Stat>
      </div>

      {investor.portfolio && investor.portfolio.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Notable investments</h2>
          <div className="flex flex-wrap gap-1.5">
            {investor.portfolio.map((p) => (
              <a
                key={(p.slug ?? p.name) + (p.sector ?? '')}
                href={p.slug ? `https://www.vcsheet.com/company/${p.slug}` : undefined}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border bg-neutral-100 text-neutral-700 border-neutral-200 hover:border-neutral-400"
              >
                <span className="font-medium">{p.name}</span>
                {p.sector && <span className="text-neutral-500">· {p.sector}</span>}
              </a>
            ))}
          </div>
        </div>
      )}

      {teammates && teammates.length > 0 && (
        <div>
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
            Other partners at the fund
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {teammates.map((t) => (
              <Link
                key={t.slug}
                to={`/admin/investors/${t.slug}`}
                className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-3 hover:border-neutral-400 transition-colors"
              >
                {t.headshotUrl ? (
                  <img src={t.headshotUrl} alt={t.name} className="h-10 w-10 rounded-full object-cover bg-neutral-100" loading="lazy" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center">
                    <User className="h-4 w-4 text-neutral-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900 truncate">{t.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{t.title ?? '—'}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {investor.sheetSlugs && investor.sheetSlugs.length > 0 && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Listed in</h2>
          <div className="flex flex-wrap gap-1.5">
            {investor.sheetSlugs.map((s) => (
              <a
                key={s}
                href={`https://www.vcsheet.com/sheet/${s}`}
                target="_blank"
                rel="noreferrer noopener"
                className="px-2 py-0.5 text-xs rounded-full border bg-neutral-100 text-neutral-700 border-neutral-200 hover:border-neutral-400"
              >
                {s.replace(/-/g, ' ')}
              </a>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
          Fund{funds.length === 1 ? '' : 's'}
        </h2>
        {funds.length === 0 && investor.fundName ? (
          <div className="rounded-lg border border-neutral-200 bg-white p-3">
            <div className="text-sm font-medium text-neutral-900">{investor.fundName}</div>
            <div className="text-xs text-neutral-500 mt-0.5">
              Not in our fund database{investor.fundSlug && ` (${investor.fundSlug})`}
            </div>
          </div>
        ) : funds.length === 0 ? (
          <div className="text-sm text-neutral-400">No fund linked.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {funds.map((f) => (
              <Link
                key={f.slug}
                to={`/admin/investors/funds/${f.slug}`}
                className="flex items-start gap-3 rounded-lg border border-neutral-200 bg-white p-3 hover:border-neutral-400 transition-colors"
              >
                {f.logoUrl ? (
                  <img
                    src={f.logoUrl}
                    alt={f.name}
                    className="h-10 w-10 rounded object-contain bg-white border border-neutral-200 p-0.5 shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-10 w-10 rounded bg-neutral-100 flex items-center justify-center shrink-0">
                    <Building2 className="h-4 w-4 text-neutral-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-900 truncate">{f.name.trim()}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {f.role && <span>{f.role}</span>}
                    {f.role && f.fundSizeText && <span className="text-neutral-300"> · </span>}
                    {f.fundSizeText && <span>{f.fundSizeText}</span>}
                  </div>
                  {f.sectors.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {f.sectors.slice(0, 3).map((s) => (
                        <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-neutral-100 text-neutral-600">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={`rounded-lg border border-neutral-200 bg-white p-3 ${wide ? 'sm:col-span-2' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-neutral-500 mb-1">{label}</div>
      {children}
    </div>
  )
}
